import React, { useRef, useState } from 'react';
import { useIsoLayoutEffect } from './useIsoLayoutEffect';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp } from './elementProps';
import './elementMarquee.css';

// The ticker. Its children are laid out once as a run, the run is repeated, and the track holding both
// slides one run's width and snaps back — which the eye reads as an endless strip. The speed is a real
// speed: the track is measured and the animation's duration set from it, so "74 pixels a second" means
// the same thing on a line of three short promises and a line of nine long ones.
//
// In the builder the track stands still and shows one run. A merchant cannot select a word that is
// sliding away from them, and the second run is the element's own business, not something to edit.

/** Which way the strip travels, exactly as `marqueeElement` offers it. */
const DIRECTIONS = ['start', 'end'] as const;

/** Below this a run is not laid out yet, and a duration computed from it would be a division by nothing. */
const MIN_RUN_WIDTH = 1;

// Measures the first run whenever it is laid out anew, so the speed survives a resize and a font swap.
function useRunWidth(): [React.RefObject<HTMLDivElement>, number] {
  const run = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [width, setWidth] = useState(0);
  useIsoLayoutEffect(() => {
    const node = run.current;
    if (!node) return;
    const read = () => setWidth(node.getBoundingClientRect().width);
    read();
    const watcher = new ResizeObserver(read);
    watcher.observe(node);
    return () => watcher.disconnect();
  }, []);
  return [run, width];
}

// Draws the ticker: the two runs on one sliding track, at the speed and in the direction the merchant set.
export const MarqueeElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing, motionPreview } = useElementRender();
  const frozen = editing && !motionPreview;
  const [run, runWidth] = useRunWidth();
  const speed = numberProp(node.props, 'speed', 74);
  const spacing = numberProp(node.props, 'spacing', 60);
  // The run's measured width already holds its trailing space, so one run's width is one cycle's distance.
  const seconds = runWidth < MIN_RUN_WIDTH ? 0 : runWidth / Math.max(speed, 1);
  const trackStyle = {
    '--xe-marquee-gap': `${spacing}px`,
    '--xe-marquee-seconds': `${seconds}s`
  } as React.CSSProperties;

  return (
    <div
      {...box}
      className={[box.className, 'xe-marquee'].filter(Boolean).join(' ')}
      data-direction={choiceProp(node.props, 'direction', DIRECTIONS, 'start')}
      data-pause={boolProp(node.props, 'pauseOnHover', false) || undefined}
      data-editing={frozen || undefined}
    >
      <div className="xe-marquee-track" style={trackStyle}>
        <div className="xe-marquee-run" ref={run}>{children}</div>
        {/* The repeat exists for the eye alone: a screen reader hears each promise once. */}
        {!frozen && <div className="xe-marquee-run" aria-hidden="true">{children}</div>}
      </div>
    </div>
  );
};
